import { zipObject } from 'lodash';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { VIRTUAL_COLUMN_TYPE_VANTAGE } from '@/serverSideTable/serverSideTable.constants';
import { DISPLAY_NAME_COLUMN_MATCH_SEGMENT } from '@/tableDefinitionEditor/tableDefinition.constants';
import { downloadBlob } from '@/utilities/download.utilities';
import { sqMaterializedTablesApi } from '@seeqdev/api/endpoint/MaterializedTablesApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { SeeqNames } from '@seeqdev/constants';
import type { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { GroupBy, VirtualColumnType } from '@/serverSideTable/serverSideTable.constants';
import type { AnyProperty } from '@/utilities.types';
import type { Filter as SdkFilter } from '@seeqdev/api/model/Filter';
import type { TableResultOutputV1 } from '@seeqdev/api/model/TableResultOutputV1';

export interface Event {
  properties: { name: string; value: unknown; unitOfMeasure?: string }[];
}

export interface GetTableParams {
  limit: number;
  columns: string[];
  properties?: AnyProperty[];
  filter?: Filter;
  sorts?: { columnName: string; ascending: boolean }[];
  cursor?: { cursorKey: string };
  useDistinct?: boolean;
  virtualColumns?: Record<VirtualColumnType, string[]>;
  groupBy?: GroupBy;
  conditionMonitorId?: string;
  /**
   * Columns to apply PostgreSQL DISTINCT ON to (requires `useDistinct: true`). Each entry must be part of the result:
   * a column in `columns`/`virtualColumns`, an item/user property requested via `properties` referenced by its
   * logical name `{uuidColumn}.properties.{propertyName}` (item) or `{uuidColumn}.user.{propertyName}` (user), a
   * table-join column pulled in via `tablesToJoin` referenced by `{uuidColumn}.{joinedTableId}_mt_join.{joinedColumnName}`,
   * or the effective display Name `{uuidColumn}.display.Name` when `tablesToJoin` is present.
   */
  distinctOnColumns?: string[];
  /** Hierarchy / stored-table joins that surface `_mt_join` alias columns in the result. */
  tablesToJoin?: { tableDefinitionId: string; columnIdsToInclude?: string[] }[];
}

export async function publish(tableId: string, events: Event[]) {
  const response = await sqMaterializedTablesApi.publishEvents(
    {
      events: events.map((event) => ({ genericEvent: event })),
    },
    { tableId },
  );
  return response.data;
}

export function tabulate({ rows, headers }: TableResultOutputV1): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);

  return rows.map((row) => zipObject(headerNames, row));
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
    useDistinct,
    virtualColumns,
    groupBy,
    conditionMonitorId,
    distinctOnColumns = [],
    tablesToJoin,
  }: GetTableParams,
  cancellationGroup?: string,
) {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }

  const { data: tableResult } = await sqTablesApi.queryTableData(
    {
      limit,
      columnsToInclude: columns,
      propertiesToInclude: properties,
      filter: filter as SdkFilter | undefined,
      sorts,
      cursor,
      useDistinct,
      virtualColumns,
      groupBy,
      conditionMonitorId,
      distinctOnColumns,
      tablesToJoin,
    },
    { tableId },
    config,
  );

  return { data: tabulate(tableResult), cursorKey: tableResult.cursorKey };
}

interface FetchDistinctValuesForColumnParams extends Pick<
  GetTableParams,
  'filter' | 'groupBy' | 'distinctOnColumns' | 'limit' | 'properties' | 'tablesToJoin'
> {
  tableDefinitionId: string;
  columnName: string;
  virtualColumnKey?: VirtualColumnType;
  extraColumnsToInclude?: string[];
  siblingVirtualColumns?: string[];
  /**
   * Vantage virtual/derived columns referenced by `filter` (e.g. the `Extinct` default filter)
   * that must be DECLARED so the backend can resolve them. Without this, a filter that constrains
   * the option list by a derived column fails with `Column '<name>' not found`.
   * Declared under the vantage key; merged (deduped) with the target/sibling virtual columns.
   */
  filterVirtualColumns?: string[];
}

/** Parses `{uuidColumn}.{joinedTableId}_mt_join.{joinedColumnName}` logical names. */
const parseTableJoinAliasColumnName = (
  logicalName: string,
): { uuidColumn: string; tableId: string; joinedColumnName: string } | null => {
  const marker = `${SeeqNames.MaterializedTables.MaterializedTableJoinSuffix}.`;
  const markerIdx = logicalName.indexOf(marker);
  if (markerIdx <= 0) {
    return null;
  }
  const beforeMarker = logicalName.substring(0, markerIdx);
  const lastDot = beforeMarker.lastIndexOf('.');
  if (lastDot <= 0) {
    return null;
  }
  const uuidColumn = beforeMarker.substring(0, lastDot);
  const tableId = beforeMarker.substring(lastDot + 1);
  const joinedColumnName = logicalName.substring(markerIdx + marker.length);
  if (!uuidColumn || !tableId || !joinedColumnName) {
    return null;
  }
  return { uuidColumn, tableId, joinedColumnName };
};

/** Parses `{uuidColumn}.display.Name` (COALESCE of join aliases then item Name). */
const parseDisplayNameColumnName = (logicalName: string): string | null => {
  const marker = DISPLAY_NAME_COLUMN_MATCH_SEGMENT;
  // Anchor on the final `.display.` so the `Name` suffix parses even when the uuid column itself contains `.display.`.
  const markerIdx = logicalName.lastIndexOf(marker);
  if (markerIdx <= 0) {
    return null;
  }
  const uuidColumn = logicalName.substring(0, markerIdx);
  const propertyName = logicalName.substring(markerIdx + marker.length);
  if (!uuidColumn || propertyName !== SeeqNames.Properties.Name) {
    return null;
  }
  return uuidColumn;
};

export async function fetchDistinctValuesForColumn<ColumnType = string>({
  tableDefinitionId,
  columnName,
  limit,
  virtualColumnKey,
  filter,
  groupBy,
  properties,
  extraColumnsToInclude = [],
  distinctOnColumns = [],
  siblingVirtualColumns = [],
  filterVirtualColumns = [],
  tablesToJoin,
}: FetchDistinctValuesForColumnParams): Promise<ColumnType[]> {
  const virtualColumns: Partial<Record<VirtualColumnType, string[]>> = {};
  if (virtualColumnKey) {
    virtualColumns[virtualColumnKey] = [columnName, ...siblingVirtualColumns];
  }
  if (filterVirtualColumns.length > 0) {
    const key = virtualColumnKey ?? VIRTUAL_COLUMN_TYPE_VANTAGE;
    virtualColumns[key] = [...new Set([...(virtualColumns[key] ?? []), ...filterVirtualColumns])];
  }

  // Item/user property paths (e.g. `item id.properties.Name`) are not base columns — request the UUID
  // column via propertiesToInclude and DISTINCT ON the property alias (see MaterializedTablesContractTest).
  // Table-join alias paths (`*_mt_join.*`) similarly need the UUID column + tablesToJoin + DISTINCT ON.
  // `.display.Name` needs the UUID column + tablesToJoin (+ Name via CTE) + DISTINCT ON the display alias.
  const propertyUuidColumns = (properties ?? [])
    .map((property) => property.uuidColumn as string | undefined)
    .filter((uuidColumn): uuidColumn is string => !!uuidColumn);
  const tableJoinAlias = parseTableJoinAliasColumnName(columnName);
  const displayNameUuid = parseDisplayNameColumnName(columnName);
  const metadataUuidColumns = [
    ...(tableJoinAlias ? [tableJoinAlias.uuidColumn] : []),
    ...(displayNameUuid ? [displayNameUuid] : []),
  ];
  const metadataColumns = [...new Set([...propertyUuidColumns, ...metadataUuidColumns])];
  const columns = virtualColumnKey
    ? extraColumnsToInclude
    : metadataColumns.length > 0
      ? [...new Set([...metadataColumns, ...extraColumnsToInclude])]
      : [columnName, ...extraColumnsToInclude];
  const effectiveDistinctOnColumns =
    distinctOnColumns.length > 0 ? distinctOnColumns : metadataColumns.length > 0 ? [columnName] : [];

  const response = await getTable(tableDefinitionId, {
    limit,
    useDistinct: true,
    columns,
    properties,
    virtualColumns:
      Object.keys(virtualColumns).length > 0 ? (virtualColumns as Record<VirtualColumnType, string[]>) : undefined,
    filter,
    groupBy,
    distinctOnColumns: effectiveDistinctOnColumns,
    tablesToJoin,
  });

  return response.data.map((item) => item[columnName]);
}

export const MAX_CSV_EXPORT_ROWS = 100_000;

/**
 * Exports table data as a CSV file, respecting the provided filters and sorts, up to
 * MAX_CSV_EXPORT_ROWS rows.
 *
 * @returns `true` if the export was truncated at the row limit, `false` otherwise.
 */
export async function exportTableToCsv(
  tableId: string,
  params: {
    filter?: Filter;
    sorts?: { columnName: string; ascending: boolean }[];
    columnsToInclude?: string[];
    virtualColumns?: { vantage: string[] };
    conditionMonitorId?: string;
    columnHeaders?: Record<string, string>;
    columnOrder?: string[];
  },
  fallbackFilename?: string,
): Promise<boolean> {
  const response = await getAxiosInstance().post(
    `${APPSERVER_API_PREFIX}/tables/${tableId}/data/csv`,
    {
      limit: MAX_CSV_EXPORT_ROWS,
      filter: params.filter as unknown as SdkFilter,
      sorts: params.sorts,
      columnsToInclude: params.columnsToInclude,
      virtualColumns: params.virtualColumns,
      conditionMonitorId: params.conditionMonitorId,
      columnHeaders: params.columnHeaders,
      columnOrder: params.columnOrder,
    },
    { responseType: 'blob', headers: { Accept: 'text/csv' } },
  );

  downloadBlob({ blob: response.data as Blob, fallbackFilename });
  return response.headers['x-sq-export-truncated'] === 'true';
}
